import React, { Suspense, lazy } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Hidden } from '@material-ui/core';
import MobileFooter from './components/MobileFooter';
import { Route, Switch } from 'react-router-dom';
import ProtectedRoute from '../auth/protectedRoute';
import useSimplyTeam from '../../pch/hooks/useSimplyTeam';
import PchLoading from '../../pch/components/PchLoading';
import TopBar from './components/TopBar';
import SuspenseLoader from './SuspenseLoader';
import Footer from './components/Footer';
import { useInactivityMonitor } from '../../hooks/useInactivityMonitor';
import PolicyUpdateBanner from './components/PolicyUpdateBanner';

const Loader = (Component) => (props) =>
    (
        <Suspense fallback={<SuspenseLoader />}>
            <Component {...props} />
        </Suspense>
    );

const Help = Loader(lazy(() => import('../main/apps/help/Help')));
const Payments = Loader(lazy(() => import('../main/apps/payments/Payments')));
// const LoanAnalysis = Loader(lazy(() => import('../main/apps/loans/LoanAnalysis')));
const PCAProcess = Loader(lazy(() => import('../main/apps/loans/PCAProcess')));
const LoanDetailCustomer = Loader(lazy(() => import('../main/apps/loans/LoanDetailCustomer')));
const LoanDetailSB = Loader(lazy(() => import('../main/apps/loans/LoanDetailSB')));
const LoanHistoryTable = Loader(lazy(() => import('../main/apps/loans/LoanHistoryTable')));
const DecisionLogicRequest = Loader(
    lazy(() => import('../main/apps/decisionLogic/DecisionLogicRequest'))
);
const DecisionLogicCallback = Loader(
    lazy(() => import('../main/apps/decisionLogic/DecisionLogicCallback'))
);
const PeopleDetail = Loader(lazy(() => import('../main/apps/people/PeopleDetail')));
const People = Loader(lazy(() => import('../main/apps/people/People')));
const ProposalVerification = Loader(
    lazy(() => import('../main/apps/proposals/ProposalVerification'))
);
const Proposals = Loader(lazy(() => import('../main/apps/proposals/Proposals')));
const Profile = Loader(lazy(() => import('../main/apps/people/Profile')));
const DeleteInformation = Loader(lazy(() => import('../main/apps/people/DeleteInformation')));
const ProfileUpdated = Loader(lazy(() => import('../main/apps/people/ProfileUpdated')));
const VerifyEmail = Loader(lazy(() => import('../main/apps/pages/VerifyEmail')));
const VerifiedEmail = Loader(lazy(() => import('../main/apps/pages/VerifiedEmail')));
const VerificationError = Loader(lazy(() => import('../main/apps/proposals/VerificationError')));
const ErrorPage = Loader(lazy(() => import('../main/apps/pages/ErrorPage')));
const DashBoard = Loader(lazy(() => import('../main/apps/dashboard/DashBoard')));
const MarketingLinks = Loader(lazy(() => import('../main/apps/marketing/MarketingLinks')));
const LoanAnalysis = Loader(lazy(() => import('../main/apps/loans/LoanAnalysis')));
const Settings = Loader(lazy(() => import('../main/apps/settings/SettingsHome')));
const Unauthorized = Loader(lazy(() => import('../main/apps/pages/Unauthorized')));
const CWAResults = Loader(lazy(() => import('../main/apps/cwa/CWAResults')));

const ConsentLink = Loader(lazy(() => import('../main/apps/cwa/ConsentLink')));
const ConsentInApp = Loader(lazy(() => import('../main/apps/cwa/ConsentInApp')));
const ConsentLegacyLink = Loader(lazy(() => import('../main/apps/cwa/ConsentLegacyLink')));

function Layout(props) {
    const { loadingSB, sb } = useSimplyTeam();
    useInactivityMonitor(sb);
    return (
        <div>
            <CssBaseline />
            <TopBar />
            <main className="mt-28">
                <div className="px-8">
                    {sb === false && <PolicyUpdateBanner />}
                    {loadingSB ? (
                        <PchLoading />
                    ) : (
                        <Switch>
                            {/* Credit check consent: the email link needs no login, the token is the proof */}
                            <Route path="/cwa/consent/:token" component={ConsentLink} exact />
                            <ProtectedRoute
                                path="/cwa/:loanId/confirm"
                                component={ConsentInApp}
                                exact
                            />
                            <Route
                                path={['/cwa/:loanId/approve', '/cwa/:loanId/reject']}
                                component={ConsentLegacyLink}
                                exact
                            />
                            <ProtectedRoute path="/cwa/:loanId" component={CWAResults} exact />
                            <ProtectedRoute path="/settings" component={Settings} exact />
                            <ProtectedRoute
                                path="/marketing/links"
                                component={MarketingLinks}
                                exact
                            />
                            <ProtectedRoute path="/help" component={Help} exact />
                            <ProtectedRoute path="/payments" component={Payments} exact />
                            <ProtectedRoute
                                path="/loans/:loanId/analysis"
                                component={LoanAnalysis}
                            />
                            <ProtectedRoute path="/loans/:loanId/pca" component={PCAProcess} />
                            <ProtectedRoute
                                path="/loan/:resourceId/history"
                                component={LoanHistoryTable}
                            />
                            <ProtectedRoute
                                path="/loan/:resourceId"
                                component={LoanDetailCustomer}
                                exact
                            />
                            <ProtectedRoute path="/loans/:loanId" component={LoanDetailSB} exact />
                            <ProtectedRoute
                                path="/dl/finish/:proposalId"
                                component={DecisionLogicRequest}
                                exact
                            />
                            <ProtectedRoute
                                path="/dl/:action"
                                component={DecisionLogicCallback}
                                exact
                            />
                            <ProtectedRoute path="/dl" component={DecisionLogicRequest} exact />
                            <ProtectedRoute
                                path="/people/:peopleId"
                                component={PeopleDetail}
                                exact
                            />
                            <ProtectedRoute path="/people/" component={People} exact />
                            <ProtectedRoute
                                path="/proposals/:resourceId/verification"
                                component={ProposalVerification}
                                exact
                            />
                            <ProtectedRoute
                                path="/proposal/verification/failed"
                                component={VerificationError}
                                exact
                            />
                            <ProtectedRoute
                                path="/proposals/:resourceId"
                                component={Proposals}
                                exact
                            />
                            <ProtectedRoute path="/proposals" component={Proposals} exact />
                            <ProtectedRoute
                                path="/profile/updated"
                                component={ProfileUpdated}
                                exact
                            />
                            <ProtectedRoute path="/profile" component={Profile} exact />
                            <ProtectedRoute
                                path="/profile/delete"
                                component={DeleteInformation}
                                exact
                            />
                            <Route path="/email-verified" component={VerifiedEmail} exact />
                            <ProtectedRoute path="/verify-email" component={VerifyEmail} exact />
                            <ProtectedRoute path="/unauthorized" component={Unauthorized} exact />
                            <ProtectedRoute path="/" component={DashBoard} exact />
                            <ProtectedRoute path="*">
                                <ErrorPage />
                            </ProtectedRoute>
                        </Switch>
                    )}
                </div>

                <Footer />
                <Hidden lgUp>
                    <MobileFooter />
                </Hidden>
            </main>
        </div>
    );
}

export default Layout;
